import React, { FC, useEffect, useState } from 'react';
import Button from '@/components/DesignLibrary/Button';
import Spacer from '@/components/Spacer';
import { CurrencyUnknown, CurrencyValue } from '@/typings/api-models';
import { ApiDefinition } from '@documentsApi';
import { calculateMeansTest } from '@/tsClient/ai';
import { useToast } from '@/hooks';
import { ToastTypeVariants } from '@/context/ToastContext';
import AILabel from '@/components/AILabel';
import Modal from '@/components/Modal';
import { MeansTestResponse } from '@/tsClient/servers/noodleDocuments/document-api-models';
import DOMPurify from 'isomorphic-dompurify';
import NoodleAI from '@/components/Icons/NoodleAI';
import Shimmer from '@/components/Shimmer';
import BannerStatus from '@/components/DesignLibrary/BannerStatus';
import { JobType, useJobContext } from '@/providers/Jobs';
import { SocketMessageType } from '@/providers/Socket';

import s from './DocumentRequestUserDetail.module.scss';
import CalculatorTable from './CalculatorTable';

type Props = {
  meansTestData: MeansTestResponse | null;
  tableData: ApiDefinition['/document-request-users/{documentRequestUserId}/consolidated-data']['GET']['response'] | null;
  selectedForMeanTest: string[];
  userWorkflowId: string | null;
  documentRequestUserId: string;
  fetchingMeansTestData: boolean
};

type RequestPayload = {
  'Amount per Pay Period': number | null | CurrencyUnknown;
  'Overtime per Pay Period': number | null;
  'Pay Date': string | null;
  Employer: string | null;
  type: string | null;
};

const MeansTestSection: FC<Props> = ({
  meansTestData,
  tableData,
  selectedForMeanTest,
  userWorkflowId,
  documentRequestUserId,
  fetchingMeansTestData,
}) => {
  const [meansTestResponse, setMeansTestResponse] = useState<MeansTestResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [isReasoningModalOpen, setIsReasoningModalOpen] = useState(false);
  const toast = useToast();
  const { addJob } = useJobContext();

  const handleMeansTestCalculation = async (): Promise<void> => {
    if (!tableData?.files?.length || !selectedForMeanTest?.length) return;
    if (!userWorkflowId) {
      toast(ToastTypeVariants.ERROR, 'User workflow id is missing');
      return;
    }
    const payload: RequestPayload[] = [];
    tableData.files.forEach(file => {
      if (file.documents?.length && file.metadata?.incomeSource === "Employment") {
        file.documents?.forEach(doc => {
          if (selectedForMeanTest.includes(doc.id)) {
            payload.push({
              'Amount per Pay Period': (doc.data?.tableData as Record<string, number>).amountPerPayPeriod,
              Employer: file.fileName,
              'Overtime per Pay Period': (doc.data?.tableData as Record<string, number>).overtimePerPayPeriod,
              'Pay Date': (doc.data?.tableData as Record<string, string>).payDate,
              type: 'Employment',
            });
          }
        });
      } else if (
        file.metadata
        && file.metadata.incomeSource
        && file.metadata.incomeSource !== 'Employment'
        && file.metadata.monthlyDollarValue.length
      ) {
        file.metadata.monthlyDollarValue.forEach((value: { month: string; income: CurrencyValue; }) => {
          if( selectedForMeanTest.includes(`${file.id}-${value.month}`)) {
            payload.push({
              'Amount per Pay Period': value.income.amount,
              Employer: null,
              'Overtime per Pay Period': 0,
              'Pay Date': value.month,
              type: file.fileName,
            });
          }
        });

      }
    });

    try {
      setLoading(true);
      setMeansTestResponse(null);
      addJob({
        correlationId: `ai-means-test-data/${documentRequestUserId}`,
        function: async () => {
          // run function indefinitely until socket message is received
          const interval = setInterval(() => {}, 1000);
          return new Promise(() => {}).finally(() => clearInterval(interval));
        },
        metadata: { messageType: SocketMessageType.MEANS_TEST_AI_REQUEST_STATUS_UPDATED },
        title: 'Means Test Calculation',
        type: JobType.AI_JOB,
      });
      const response = await calculateMeansTest({ data: payload, documentRequestUserId, userWorkflowId });
      setMeansTestResponse(response);
    } catch (error) {
      toast(ToastTypeVariants.ERROR, 'Ai failed to calculate means test');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (meansTestData) {
      setMeansTestResponse(meansTestData);
    }
  }, [meansTestData]);

  return (
    <div className={s.meansTestSectionContainer}>
      {isReasoningModalOpen && (
        <Modal title={<span className={s['title--ai']}>Means Test Calculator AI Reasoning</span>} onClose={() => setIsReasoningModalOpen(false)} drag>
          <div className={s.reasoningModalContent}>
            {loading || fetchingMeansTestData || meansTestResponse?.status === 'pending'
              ? (
                <>
                  <Spacer />
                  <AILabel label="Glade ai is calculating..." />
                </>
              )
              : (
                meansTestResponse?.data?.calculationExplanationHtml && (
                  <div
                    className={s.explanationHtml}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(meansTestResponse?.data?.calculationExplanationHtml),
                    }}
                  />
                )
              )}
            <Spacer />
            {(!loading && !fetchingMeansTestData && meansTestResponse?.status !== 'pending') && <Button variant="ai" size="sm" fullWidth disabled={loading} onClick={handleMeansTestCalculation}>
              Recalculate
            </Button>}
          </div>
        </Modal>
      )}
      <div className="caption-bold">Means Test Calculator</div>
      {fetchingMeansTestData && <><Spacer/><Shimmer /></>}
      {(loading || meansTestResponse?.status === 'pending' || meansTestResponse?.status === 'failed') && !fetchingMeansTestData
        ? (
          <>
            <Spacer />
            {meansTestResponse?.status === 'pending' && <div className={s.aiTopBanner}>
              <NoodleAI size={32} />
              <div className={s.textContainer}>
                <div>Glade Ai is calculating...</div>
                <div className={s.secondaryText}>You can leave the page.</div>
              </div>
            </div>}
            {meansTestResponse?.status === 'failed' && <>
              <BannerStatus status='error' label='AI failed to generate means test data' cta={{label:"Recalculate", onClick:handleMeansTestCalculation}} />
            </>}
          </>
        )
        : (
          <>
            {!!meansTestResponse && meansTestData && !!Object.keys(meansTestResponse?.data).length && (
              <>
                <Spacer />
                <CalculatorTable data={meansTestData} />
              </>
            )}
            <Spacer />
            {!fetchingMeansTestData && <div className={s.ctaSection}>
              {!!meansTestResponse && !!Object.keys(meansTestResponse?.data).length && (
                <Button variant="secondary" size="sm" onClick={() => setIsReasoningModalOpen(true)}>
                  View Reasoning
                </Button>
              )}
              <Button variant="ai" size="sm" onClick={handleMeansTestCalculation}>
                {( meansTestResponse && Object.keys(meansTestResponse.data).length ) ? 'Recalculate' : 'Calculate'}
              </Button>
            </div>}

          </>
        )}
    </div>
  );
};

export default MeansTestSection;
